import { Menu } from 'lucide-react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui';
import { LANDING_COMPACT_MENU_ITEMS } from '@/constants/landingCompact';
import { MODULE_PAGE_GROUPS } from '@/constants/modulePages';
import { getModulePagePath, getModulePagesByGroup } from '@/lib/modulePages';
import type { ModulePageSlug } from '@/types/modulePages';

interface LandingCompactMenuProps {
  onSelect: (key: (typeof LANDING_COMPACT_MENU_ITEMS)[number]) => void;
  activeSlug?: ModulePageSlug;
}

export const LandingCompactMenu = ({ onSelect, activeSlug }: LandingCompactMenuProps) => {
  const { t } = useTranslation();

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button type='button' variant='mktIcon' size='mktIcon' aria-label={t('landingPage.mobile.menu')}>
          <Menu className='h-[18px] w-[18px]' aria-hidden='true' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align='end'
        sideOffset={12}
        className='max-h-[min(80vh,640px)] min-w-[240px] overflow-y-auto rounded-[14px] p-1.5 font-mkt-sans'
      >
        {MODULE_PAGE_GROUPS.map((group) => (
          <div key={group.key}>
            <DropdownMenuLabel className='px-3 pb-1 pt-2.5 font-mkt-mono text-[10.5px] font-medium tracking-[.2em] text-mkt-sand-text'>
              {t(group.titleKey)}
            </DropdownMenuLabel>
            {getModulePagesByGroup(group.key).map((module) => (
              <DropdownMenuItem
                key={module.slug}
                asChild
                className='rounded-[10px] px-3 py-2 text-[15px] text-mkt-ink aria-[current=page]:font-semibold aria-[current=page]:text-mkt-blue'
              >
                <Link
                  to={getModulePagePath(module.slug)}
                  aria-current={module.slug === activeSlug ? 'page' : undefined}
                >
                  {t(module.labelKey)}
                </Link>
              </DropdownMenuItem>
            ))}
          </div>
        ))}
        <DropdownMenuSeparator className='mx-1.5 my-1.5 bg-mkt-ink/8' />
        {LANDING_COMPACT_MENU_ITEMS.map((key) => (
          <DropdownMenuItem
            key={key}
            onSelect={() => onSelect(key)}
            className='rounded-[10px] px-3 py-2.5 text-[15px] text-mkt-ink'
          >
            {t(`landingPage.roadmap.${key}`)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
